import { queryClient } from "@shared/api/queries/queryClient";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useBillCycleConsumerDashboard,
  useBillCycleDetailsForEdit,
  useCategoryConfigMap,
  useConsumerList,
  useSubCategoryConfigMap,
  useSubmitBillCycle,
  useUpdateBillCycle,
} from "@features/bx/bill-cycle/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { PremiseTable } from "./PremiseTable";
import { useChoices, usePremisesForBillCycle } from "../hooks";
import { useDebounce } from "@shared/hooks/useDebounce";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { PaginatedResponsePremise } from "../types";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";

const CreateUpdateCycle = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: categoryConfigMap = [], isPending: isCategoryLoading } =
    useCategoryConfigMap();
  const [subCategoryOptions, setSubCategoryOptions] = useState<any[]>([]);
  const { data: billingPeriodsChoices } = useChoices("billing_period");
  const billingPeriods = mapToLabelValue(billingPeriodsChoices);

  const { data: billCycleDetails } = useBillCycleDetailsForEdit(
    params.action === "edit" ? params.id : undefined
  );

  const isNotEditable = billCycleDetails?.statusDisplay?.toLowerCase() === "archive";
  const formFields: FormField[] = [
    {
      name: "billCycleName",
      label: "Bill Cycle Name",
      type: "text",
      required: true,
      disabled: params.action === "edit" || isNotEditable,
      placeholder: "e.g. Cycle A – North Zone",
      className: "col-span-1",
    },
    {
      name: "frequency",
      label: "Billing Period",
      type: "select",
      required: true,
      options: billingPeriods,
      disabled: isNotEditable,
      placeholder: "Select billing period",
      className: "col-span-1",
    },
    {
      name: "category",
      label: "Consumer Category",
      type: "multi-select",
      required: true,
      options: categoryConfigMap.map((category: any) => ({
        value: category.value,
        label: category.label,
      })),
      placeholder: "Select category",
      className: "col-span-1",
      disabled: isNotEditable,
    },
    {
      name: "subCategory",
      label: "Sub Category",
      type: "multi-select",
      required: true,
      options: subCategoryOptions,
      placeholder: "Select sub category",
      className: "col-span-1",
      disabled: isNotEditable,
    },
    {
      name: "cycleDuration",
      label: "Bill Distribution Days",
      type: "text",
      required: false,
      placeholder: "e.g. 7",
      className: "col-span-1",
      customValidation: (value: any) => {
        // ✅ allow empty / undefined / null
        if (value === "" || value === null || value === undefined) {
          return true;
        }

        const numValue = Number(value);

        if (Number.isNaN(numValue)) {
          return "Please enter a valid number";
        }

        if (numValue < 0) {
          return "Duration cannot be negative";
        }

        if (!Number.isInteger(numValue)) {
          return "Please enter a whole number of days";
        }

        if (numValue === 0) {
          return "Duration must be at least 1 day";
        }

        return true;
      },
      disabled: isNotEditable,
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const watchedValues = form.watch();
  const [selectedPremises, setSelectedPremises] = useState<string[]>([]);

  const { data: subCategoryList = [], isPending: isSubCategoryLoading } =
    useSubCategoryConfigMap(
      watchedValues.category &&
        Array.isArray(watchedValues.category) &&
        watchedValues.category.length > 0
        ? watchedValues.category
        : []
    );

  useEffect(() => {
    setSubCategoryOptions(
      subCategoryList.map((subCategory: any) => ({
        value: subCategory.id,
        label: subCategory.name,
      }))
    );
  }, [subCategoryList]);

  const handleFieldChange = (field, value) => {
    if (field === "category") {
      form.setValue("subCategory", []);
      setSelectedPremises([]);
    }
  };
  const [currentPremiseList, setCurrentPremiseList] =
    useState<PaginatedResponsePremise>();
  const [areaFilter, setAreaFilter] = useState<string>("all");
  const [subAreaFilter, setSubAreaFilter] = useState<string>("all");

  useEffect(() => {
    if (params.action === "edit" && billCycleDetails?.billCycleName) {
      console.log('billCycleDetails',billCycleDetails)
      form.setValue("billCycleName", billCycleDetails?.billCycleName);
      form.setValue("frequency", billCycleDetails?.frequency.toString());
      form.setValue("category", billCycleDetails?.category);
      form.setValue("subCategory", billCycleDetails?.subCategory);
      form.setValue("cycleDuration", billCycleDetails?.cycleDuration);
      setSelectedPremises(billCycleDetails?.premise);
    }
  }, [params.action, billCycleDetails, form, billingPeriodsChoices]);

  const { mutate: updateBillCycle, isPending: isUpdating } = useUpdateBillCycle(
    params.id
  );
  const { mutate: submitBillCycle, isPending: isCreating } =
    useSubmitBillCycle();

  const handlePremiseSelectionChange = useCallback((selectedIds: any) => {
    setSelectedPremises(selectedIds);
  }, []);

  const debouncedPremises = useDebounce(selectedPremises, 1000);

  const { data: consumerDashboard } = useBillCycleConsumerDashboard({
    premise: debouncedPremises,
  });
  const handleSave = async () => {
    const isValidated = await FormService.validateForm(form);
    if (!isValidated) return;

    if (selectedPremises?.length === 0) {
      toast({
        title: "Add premise first.",
        variant: "destructive",
      });
      return;
    }

    const formValues = form.getValues();

    // Prepare selectedItemsDetails once
    const selectedItemsDetails = selectedPremises.map((code) => {
      const item = currentPremiseList?.results?.find(
        (entry) => entry.code === code
      );
      return {
        premise: code,
        area: item?.hierarchyCodes?.area || null,
        sub_area: item?.hierarchyCodes?.subArea || null,
      };
    });

    let submitPayload: any;

    if (params.action === "edit") {
      // Edit mode: only send changed fields
      const originalData = {
        ...billCycleDetails,
        frequency: String(billCycleDetails?.frequency)
      };
      const editPayload = FormService.getUpdatedFields(originalData, formValues);

      // Check if premises changed
      const originalPremises = billCycleDetails?.premise || [];
      const premiseChanged =
        JSON.stringify([...originalPremises].sort()) !==
        JSON.stringify([...selectedPremises].sort());

      // Only add premise-related fields if premises changed
      if (premiseChanged) {
        editPayload.premise = selectedPremises;
        editPayload.area = selectedItemsDetails.map((i) => i.area);
        editPayload.sub_area = selectedItemsDetails.map((i) => i.sub_area);
        editPayload.consumer_count = consumerDashboard?.total;
      }
      submitPayload = editPayload;
    } else {
      // Create mode: send full payload
      submitPayload = {
        bill_cycle_name: formValues.billCycleName,
        utility_services: [],
        category: formValues.category,
        sub_category: formValues.subCategory,
        frequency: formValues.frequency,
        premise: selectedPremises,
        area: selectedItemsDetails.map((i) => i.area),
        sub_area: selectedItemsDetails.map((i) => i.sub_area),
        consumer_count: consumerDashboard?.total,
        remote_utility_id: getRemoteUtilityId(),
      };

      if (formValues?.cycleDuration) {
        submitPayload.cycle_duration = formValues.cycleDuration;
      }
    }

    // Select the appropriate mutation
    const mutation = params.action === "edit" ? updateBillCycle : submitBillCycle;

    // Call the mutation
    mutation(submitPayload);
  };

  // Calculate totals from selected premises
  const totalPremises = selectedPremises.length;
  const totalConsumers = consumerDashboard?.total || 0;
  const totalMeters = selectedPremises.length; // Assuming 1 meter per premise
  const buttonLabel = isNotEditable
    ? "Archived Cycle"
    : params.action === "edit"
      ? "Update Bill Cycle"
      : "Create Bill Cycle";
  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          {params.action === "edit" ? "Edit Bill Cycle" : "Create Bill Cycle"}
        </h1>
      </div>

      {/* Main content with 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Basic Information */}
        <div className="col-span-1">
          <Card className="shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold">
                Basic Information
              </CardTitle>
              <CardDescription className="text-sm text-gray-500">
                Essential details to identify the bill cycle.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DynamicForm
                fields={formFields}
                form={form}
                onSubmit={handleSave}
                gridColumns={1}
                onFieldChange={handleFieldChange}
                responsiveColumns={{ sm: 1, md: 1 }}
                config={{
                  layout: "vertical",
                  spacing: "small",
                  showSubmitButton: false,
                  showResetButton: false,
                  classes: FIELD_CLASSES,
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Premise Selection */}
        <div className="col-span-2">
          <Card className="shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold">
                Premise Selection
              </CardTitle>
              <CardDescription className="text-sm text-gray-500">
                Select the premises to include in this bill cycle.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-xs text-gray-600 mb-1">
                    Total Premises
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {currentPremiseList?.count ?? 0}
                  </div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <div className="text-xs text-gray-600 mb-1">
                    Premises Selected
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {totalPremises}
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-xs text-gray-600 mb-1">
                    Total Consumer
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {consumerDashboard?.consumersCount?.total ?? 0}
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-xs text-gray-600 mb-1">Total Meter</div>
                  <div className="text-2xl font-bold text-purple-600">
                    {consumerDashboard?.premiseMeters?.total ?? 0}
                  </div>
                </div>
              </div>

              {/* Selected count */}
              {/* <div className="text-sm text-gray-600 flex gap-3 items-center">
                <Button size="sm" variant="outline">Select All Premises</Button>
              </div> */}

              {/* Premises Table */}
              <div className="border rounded-lg overflow-scroll max-h-[70vh] p-4">
                <PremiseTable
                  disableSelection={isNotEditable}
                  selectedCategories={watchedValues.category}
                  selectedSubCategories={watchedValues.subCategory}
                  selectedPremises={selectedPremises}
                  onSelectionChange={handlePremiseSelectionChange}
                  onPremiseListChange={setCurrentPremiseList}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Action buttons at bottom */}
      <div className="flex justify-end gap-3 mt-6">
        <Button
          variant="outline"
          onClick={() => navigate("/bx/bill-setup/billcycle")}
        >
          Cancel
        </Button>
        <Button disabled={isNotEditable} onClick={handleSave} isLoading={isUpdating || isCreating}>
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
};

export default CreateUpdateCycle;
